'use client';

import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CaptionsTranscript, TranscriptEntry } from '@osmosis/shared/video';

import { track } from '../../../../lib/util/tracking';
import { usePagesContext } from '@osmosis/nextjs-components/context/PagesProvider';
import { useAIPanelContext } from '../../../../context/AIPanelContext';
import { useVideoPlayer } from '../../../../context/VideoPlayerProvider';
import { parseHtmlToText } from '../../../../lib/util/oai';
import BlurredBlockAuth from '../BlurredBlockAuth';
import WatchVideoOnlyLink from '../WatchVideoOnlyLink';
import { truncateCaptionsTranscript } from './truncateCaptionsTranscript';
import { useCaptionsSearch } from './useCaptionsSearch';
import { usePlaybackSync } from './usePlaybackSync';
import ChaptersBar from './ChaptersBar';
import SearchBar from './SearchBar';
import SyncPill from './SyncPill';
import TranscriptEntries from './TranscriptEntries';

type Props = {
  captionsTranscript: CaptionsTranscript;
  name?: string;
  videoId?: number;
  isLoggedInView?: boolean;
};

/**
 * Interactive chapter-grouped transcript: chapter chips that scroll to their
 * section, a search field that highlights matches in place (Cmd+F style,
 * jump-to-next-match), and clickable timestamps that seek the video.
 *
 * This orchestrator wires shared refs and player state into the search and
 * playback-sync hooks, then composes the chapters bar, search bar, entries,
 * and floating sync pill.
 */
export default function CaptionsTranscriptClient({
  captionsTranscript,
  name,
  videoId,
  isLoggedInView,
}: Props) {
  const intl = useIntl();
  const {
    handleSeekTo,
    handlePictureInPicture,
    isPictureInPicture,
    progressState,
    type,
  } = useVideoPlayer();
  const { sendPromptOpeningPanel } = useAIPanelContext();
  const { pathname } = usePagesContext();

  const entriesContainerRef = useRef<HTMLDivElement>(null);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isBlurred, setIsBlurred] = useState(false);
  const captionsTranscriptShort =
    truncateCaptionsTranscript(captionsTranscript);

  // Mirrors the legacy lockout: logged-out users see the full transcript
  // until the lockout modal has been seen, then a blurred short preview.
  useEffect(() => {
    const updateBlur = () =>
      setIsBlurred(!!localStorage.getItem('transcriptModalSeen'));

    updateBlur();
    window.addEventListener('scroll', updateBlur);
    return () => window.removeEventListener('scroll', updateBlur);
  }, []);

  const isLocked = !isLoggedInView && isBlurred;
  const activeTranscript =
    isLocked && captionsTranscriptShort
      ? captionsTranscriptShort
      : captionsTranscript;
  const { chapters } = activeTranscript;

  const {
    activeEntry,
    activeChapterIndex,
    isSyncPillVisible,
    handleSyncToVideo,
  } = usePlaybackSync({
    chapters,
    playedMs: progressState.playedSeconds * 1000,
    entriesContainerRef,
    chipContainerRef,
    chipRefs,
  });

  const {
    search,
    query,
    chaptersWithHighlights,
    matchCount,
    isSearchBarStickable,
    searchCountLabel,
    handleSearchChange,
    handleSearchKeyDown,
    navigateSearch,
  } = useCaptionsSearch({ chapters, entriesContainerRef, videoId });

  const scrollToChapter = (index: number) => {
    sectionRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleTimestampClick = (entry: TranscriptEntry) => {
    // entry.start is milliseconds (from VTT cue times); the player seeks in seconds.
    const seconds = entry.start / 1000;
    handleSeekTo(seconds);
    // Don't scroll away from a PiP video the user is actively watching.
    if (!isPictureInPicture) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    track(
      'transcript timestamp clicked',
      { video_id: videoId, timestamp: seconds },
      ['keen', 'pendo'],
    );
  };

  const handleOAIButtonClick = (text: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    track('osmosis-ai-transcript-clicked', { page: pathname }, [
      'keen',
      'pendo',
    ]);

    const message = intl.formatMessage(
      {
        id: 'learn.captions_transcript.ai_prompt',
        defaultMessage: 'Explain this to me: "{text}"',
      },
      { text: parseHtmlToText(text) },
    );

    sendPromptOpeningPanel(message);
  };

  return (
    <Flex data-testid="captions-transcript" className="relative flex-col pt-3">
      <ChaptersBar
        chapters={chapters}
        activeChapterIndex={activeChapterIndex}
        isLocked={isLocked}
        chipContainerRef={chipContainerRef}
        chipRefs={chipRefs}
        onChapterClick={scrollToChapter}
      />

      <SearchBar
        search={search}
        query={query}
        matchCount={matchCount}
        searchCountLabel={searchCountLabel}
        isLocked={isLocked}
        isSearchBarStickable={isSearchBarStickable}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        onNavigate={navigateSearch}
        onClear={() => handleSearchChange('')}
      />

      {!isLoggedInView && name && (
        <Box className="px-3 shrink-0">
          <WatchVideoOnlyLink
            name={name}
            className="text-xs font-normal underline [&_svg]:ml-[2px] [&_svg]:underline"
          />
        </Box>
      )}

      <TranscriptEntries
        chaptersWithHighlights={chaptersWithHighlights}
        activeEntry={activeEntry}
        isLocked={isLocked}
        entriesContainerRef={entriesContainerRef}
        sectionRefs={sectionRefs}
        onTimestampClick={handleTimestampClick}
        onAskAI={handleOAIButtonClick}
        syncPill={
          isSyncPillVisible && !isLocked ? (
            <SyncPill
              videoType={type}
              isSearchBarStickable={isSearchBarStickable}
              onSync={handleSyncToVideo}
              onPictureInPicture={handlePictureInPicture}
            />
          ) : null
        }
      />

      {isLocked && (
        <BlurredBlockAuth
          text={intl.formatMessage({
            id: 'learn_block.authModal.title_message',
            defaultMessage:
              'Want to see more? Sign in or sign up to access the full transcript and additional features.',
          })}
          className="right-0 bottom-0 min-h-40"
        />
      )}
    </Flex>
  );
}
